import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { ReactNode } from 'react';
import { GroupId } from './types';

/**
 * Atom family for widget toolbar controls
 */
const widgetControlAtomFamily = atomFamily((_instanceId: string) =>
  atom<ReactNode>(undefined)
);

/**
 * Hook to set widget control (toolbar buttons/actions)
 * Use in widget to register controls
 */
export const useSetWidgetControl = (instanceId: string) => {
  return useSetAtom(widgetControlAtomFamily(instanceId));
};

/**
 * Hook to get widget control
 * Use in layout system to render controls
 */
export const useWidgetControl = (instanceId: string) => {
  return useAtomValue(widgetControlAtomFamily(instanceId));
};

/**
 * Atom family for widget settings panels
 */
const widgetSettingsAtomFamily = atomFamily((_instanceId: string) =>
  atom<ReactNode>(undefined)
);

/**
 * Hook to set widget settings panel
 * Use in widget to provide settings UI
 */
export const useSetWidgetSettings = (instanceId: string) => {
  return useSetAtom(widgetSettingsAtomFamily(instanceId));
};

/**
 * Hook to get widget settings
 * Use in layout system to render settings
 */
export const useWidgetSettings = (instanceId: string) => {
  return useAtomValue(widgetSettingsAtomFamily(instanceId));
};

/**
 * Atom family for widget context menu
 */
const widgetMenuAtomFamily = atomFamily((_instanceId: string) =>
  atom<ReactNode>(undefined)
);

/**
 * Hook to set widget context menu
 */
export const useSetWidgetMenu = (instanceId: string) => {
  return useSetAtom(widgetMenuAtomFamily(instanceId));
};

/**
 * Hook to get widget context menu
 */
export const useWidgetMenu = (instanceId: string) => {
  return useAtomValue(widgetMenuAtomFamily(instanceId));
};

/**
 * Atom family for widget grouping
 * Linked widgets share the same groupId
 */
const widgetGroupAtomFamily = atomFamily((_instanceId: string) =>
  atom<GroupId | undefined>(undefined)
);

/**
 * Hook to manage widget group (read/write)
 */
export const useWidgetGroup = (instanceId: string) => {
  return useAtom(widgetGroupAtomFamily(instanceId));
};

/**
 * Hook to get widget group ID (read-only)
 */
export const useWidgetGroupId = (instanceId: string) => {
  return useAtomValue(widgetGroupAtomFamily(instanceId));
};

/**
 * Hook to set widget group ID (write-only)
 */
export const useSetWidgetGroupId = (instanceId: string) => {
  return useSetAtom(widgetGroupAtomFamily(instanceId));
};
